import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getCollectionJobs,
  cancelCollectionJob,
  createCollectionJob,
  createClassificationOnlyJob,
  previewSearch,
  type CollectionJob,
  type CreateCollectionJobRequest,
  type PreviewSearchRequest,
  type PreviewSearchResponse,
} from "@/services/api/collection-jobs";

export const collectionJobsKeys = {
  all: ["collection-jobs"] as const,
  lists: () => [...collectionJobsKeys.all, "list"] as const,
  list: (filters?: Record<string, unknown>) =>
    [...collectionJobsKeys.lists(), { filters }] as const,
  detail: (id: string | number) =>
    [...collectionJobsKeys.all, "detail", id] as const,
};

const TERMINAL_STATUSES = new Set([2, 3, 4, 5, 6]);
const POLL_INTERVAL_MS = 5_000;

function hasActiveJob(jobs: CollectionJob[] | undefined): boolean {
  if (!jobs || jobs.length === 0) return false;
  return jobs.some((j) => !TERMINAL_STATUSES.has(j.status));
}

export function useCollectionJobs(
  options?: Omit<
    UseQueryOptions<CollectionJob[], Error>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery<CollectionJob[], Error>({
    queryKey: collectionJobsKeys.list(),
    queryFn: getCollectionJobs,
    staleTime: 30 * 1000,
    refetchInterval: (query) =>
      hasActiveJob(query.state.data) ? POLL_INTERVAL_MS : false,
    ...options,
  });
}

export function useCancelCollectionJob() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string | number>({
    mutationFn: cancelCollectionJob,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: collectionJobsKeys.all });
      toast.success("Collection job cancelled successfully");
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to cancel collection job"
      );
    },
  });
}

export function useCreateCollectionJob() {
  const queryClient = useQueryClient();

  return useMutation<CollectionJob, Error, CreateCollectionJobRequest>({
    mutationFn: createCollectionJob,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: collectionJobsKeys.all });
      toast.success("Collection job created successfully");
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to create collection job"
      );
    },
  });
}

export function useCreateClassificationOnlyJob() {
  const queryClient = useQueryClient();

  return useMutation<CollectionJob, Error, CreateCollectionJobRequest>({
    mutationFn: createClassificationOnlyJob,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: collectionJobsKeys.all });
      toast.success("Classification-only job created successfully");
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to create classification-only job"
      );
    },
  });
}

export function usePreviewSearch() {
  return useMutation<PreviewSearchResponse, Error, PreviewSearchRequest>({
    mutationFn: previewSearch,
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to preview search results"
      );
    },
  });
}
